import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import './Modal.scss'

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Modal Component
 */
const Modal = ({ open, onDismiss, id, ariaLabelledBy, children }) => {
  const [isOpen, setValue] = useState(open)
  const isMounted = useRef(false)
  const modalRef = useRef(null)
  const previouslyFocused = useRef(null)

  const closeModal = () => {
    if (onDismiss) {
      setValue(false)
      onDismiss()
    }
  }

  const getFocusableElements = () => {
    if (!modalRef.current) return []
    return Array.from(modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR))
  }

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!onDismiss) return undefined
    const onDocumentClick = event => {
      if (event.target.classList && event.target.classList[0] === 'modal-background') {
        closeModal()
      }
    }
    document.addEventListener('click', onDocumentClick)
    return () => document.removeEventListener('click', onDocumentClick)
  }, [onDismiss])

  useEffect(() => {
    if (!isOpen) return undefined
    const onKeyDown = event => {
      if (event.key === 'Escape' && onDismiss) {
        event.preventDefault()
        closeModal()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = getFocusableElements()
      if (!focusable.length) {
        event.preventDefault()
        if (modalRef.current) modalRef.current.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onDismiss, isOpen])

  useEffect(() => {
    if (open !== isOpen) {
      isMounted.current && setValue(open)
    }
  }, [isOpen, open])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined
    previouslyFocused.current = document.activeElement
    const focusable = getFocusableElements()
    if (focusable.length) {
      focusable[0].focus()
    } else if (modalRef.current) {
      modalRef.current.focus()
    }
    return () => {
      const prior = previouslyFocused.current
      // Guard teardown: jsdom may already be gone when passive effects flush after a test.
      if (
        typeof document === 'undefined' ||
        !document.body ||
        !prior ||
        typeof prior.focus !== 'function'
      ) {
        return
      }
      try {
        if (prior === document.body || document.body.contains(prior)) {
          prior.focus()
        }
      } catch (err) {
        // Ignore focus restore failures after unmount/teardown.
      }
    }
  }, [isOpen])

  return (
    <div className="ModalContainer">
      <div
        id={id}
        ref={modalRef}
        className={`modal ${isOpen ? 'is-active' : ''}`}
        role={isOpen ? 'dialog' : undefined}
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby={isOpen && ariaLabelledBy ? ariaLabelledBy : undefined}
        tabIndex={isOpen ? -1 : undefined}
      >
        <div className="modal-background" />
        {isOpen && children}
      </div>
    </div>
  )
}

Modal.propTypes = {
  /** flag to toggle modal */
  open: PropTypes.bool,
  /** function to handle on modal close */
  onDismiss: PropTypes.func,
  /** id of modal */
  id: PropTypes.string,
  /** id of visible dialog title for accessible name */
  ariaLabelledBy: PropTypes.string,
  /** children html content */
  children: PropTypes.shape({})
}

Modal.defaultProps = {
  open: false,
  // onDismiss: () => {},
  id: '',
  ariaLabelledBy: '',
  children: {}
}

export default Modal
